import React, { useState, useMemo } from 'react';
import { MdToday, MdCalendarMonth, MdReceiptLong, MdAccountBalanceWallet,
  MdShield, MdBusiness, MdAdd, MdTrendingUp, MdSummarize, MdPeople,
  MdPrint, MdKingBed, MdInventory2, MdDescription,
} from 'react-icons/md';
import { PageShell, DataTable, StatCard } from '../../components/Ui';
import { useAuth } from '../../auth/AuthContext';
import { ipApi } from '../../services/api';
import { useLiveData } from '../../hooks/useLiveData';
import { useHmsRecords } from '../../hooks/useHmsRecords';
import { useHospitalProfile } from '../../hooks/useHospitalProfile';
import { printOpReceipt } from '../../utils/hospitalPdf';
import { useEmrSettings } from '../../hooks/useEmrSettings';
import { packagesFromSettings, servicePresetsFromSettings } from '../../config/emrSettings';
import { formatDoctorLabel } from '../../utils/doctorMatch';

const PAYMENT_MODES = ['Cash', 'UPI / GPay / PhonePe', 'Debit Card', 'Credit Card', 'NEFT / Bank Transfer', 'Cheque', 'Insurance / TPA', 'Free / Waived'];
const SERVICES = [
  'OPD Consultation', 'Follow-up Consultation', 'Emergency Consultation',
  'Specialist Consultation', 'Lab / Investigations', 'Radiology (X-Ray / USG / CT)',
  'Pharmacy / Medicines', 'Minor Procedure / Dressing', 'ECG',
  'Vaccination / Injection', 'IP Advance', 'IP Part Payment', 'IP Full & Final',
  'Certificate / Report Charges', 'Ambulance', 'Other'
];
const OPD_CHARGE_PRESETS = [
  { label: 'OPD Consultation', default: 500 },
  { label: 'Follow-up', default: 300 },
  { label: 'Emergency', default: 800 },
  { label: 'Lab CBC', default: 250 },
  { label: 'Lab LFT', default: 500 },
  { label: 'Lab RFT', default: 400 },
  { label: 'Lab Urine R/M', default: 150 },
  { label: 'X-Ray Chest', default: 300 },
  { label: 'USG Abdomen', default: 700 },
  { label: 'ECG', default: 200 },
  { label: 'Dressing', default: 150 },
  { label: 'Injection', default: 100 },
];

function formatInr(n) {
  return `₹${Number(n || 0).toLocaleString('en-IN')}`;
}

export default function BillingReceipts() {
  const { user } = useAuth();
  const { hospital } = useHospitalProfile();
  const canExpenses = user?.role === 'manager' || user?.role === 'billing';
  const canAnalytics = user?.role === 'manager' || user?.role === 'billing';
  const canQueue = ['manager', 'reception', 'doctor', 'nurse', 'billing', 'ipd', 'pharmacy'].includes(user?.role);
  const { settings: emrSettings } = useEmrSettings();
  const servicePresets = useMemo(() => servicePresetsFromSettings(emrSettings), [emrSettings]);
  const defaultLine = servicePresets[0] || { label: 'OPD Consultation', amount: 500 };
  const { data, loading, error, reload } = useLiveData(() => ipApi.getReceipts(80), []);
  const { data: queueData } = useLiveData(() => (canQueue ? ipApi.getQueue({ limit: 120 }) : Promise.resolve({ queue: [] })), [canQueue]);
  const activeQueue = (queueData?.queue || []).filter((q) => q.status !== 'Completed' && q.status !== 'Cancelled');
  const { data: analytics } = useLiveData(() => (canAnalytics ? ipApi.getAnalytics({ period: 'today' }) : Promise.resolve(null)), [canAnalytics]);
  const { data: monthData } = useLiveData(() => (canAnalytics ? ipApi.getAnalytics({ period: 'month' }) : Promise.resolve(null)), [canAnalytics]);
  const receipts = data?.receipts || [];
  const [tab, setTab] = useState('receipts'); // 'receipts' | 'ipdbills' | 'closing' | 'expense' | 'docrevenue' | 'insurance'
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [filter, setFilter] = useState('all');
  const [form, setForm] = useState({
    patientName: '', patientId: '', patientPhone: '', doctor: '', queueId: '',
    paymentMode: 'Cash', txnRef: '', notes: '', gstPct: 0,
    splitPay: false, pay2Mode: 'UPI / GPay', pay2Amount: '',
    lines: [{ service: defaultLine.label, desc: '', amount: defaultLine.amount }],
  });

  const addLine = () => setForm((f) => ({ ...f, lines: [...f.lines, { service: 'Lab / Investigations', desc: '', amount: '' }] }));
  const removeLine = (i) => setForm((f) => ({ ...f, lines: f.lines.filter((_, idx) => idx !== i) }));
  const updateLine = (i, patch) => setForm((f) => ({ ...f, lines: f.lines.map((l, idx) => idx === i ? { ...l, ...patch } : l) }));
  const lineTotal = form.lines.reduce((s, l) => s + Number(l.amount || 0), 0);

  const createReceipt = async (e) => {
    e.preventDefault();
    if (!form.patientName.trim()) { setMsg('Patient name is required.'); return; }
    if (lineTotal <= 0) { setMsg('Add at least one charge.'); return; }
    setBusy(true);
    setMsg('');
    try {
      const billable = form.lines.filter((l) => Number(l.amount) > 0);
      const payments = form.splitPay && Number(form.pay2Amount) > 0
        ? [
          { mode: form.paymentMode, amount: Math.max(0, lineTotal - Number(form.pay2Amount)) },
          { mode: form.pay2Mode, amount: Number(form.pay2Amount) },
        ]
        : undefined;
      const r = await ipApi.createOpReceipt({
        patientName: form.patientName.trim(),
        patientId: form.patientId || undefined,
        patientPhone: form.patientPhone || undefined,
        doctor: form.doctor || undefined,
        queueId: form.queueId || undefined,
        amount: lineTotal,
        paymentMode: form.paymentMode,
        txnRef: form.txnRef || undefined,
        service: billable.map((l) => l.service).join(', '),
        desc: billable.map((l) => `${l.service}${l.desc ? `: ${l.desc}` : ''} ₹${l.amount}`).join(' | '),
        lines: billable.map((l) => ({
          service: l.service,
          desc: l.desc || '',
          amount: Number(l.amount),
        })),
        notes: form.notes,
        gstPct: Number(form.gstPct || 0),
        hsn: '9993',
        payments,
        hospitalId: user?.hospitalId,
      });
      const receipt = r?.receipt;
      if (receipt) {
        printOpReceipt({
          ...receipt,
          amount: lineTotal,
          gstPct: Number(form.gstPct || 0),
          service: billable.map((l) => l.service).join(', '),
          payments,
        }, hospital, user);
      }
      setForm({ patientName: '', patientId: '', patientPhone: '', doctor: '', queueId: '', paymentMode: 'Cash', txnRef: '', notes: '', gstPct: 0, splitPay: false, pay2Mode: 'UPI / GPay', pay2Amount: '', lines: [{ service: defaultLine.label, desc: '', amount: defaultLine.amount }] });
      setShowForm(false);
      setMsg(`✓ Receipt created & printed — Total: ₹${lineTotal.toLocaleString('en-IN')}`);
      await reload();
    } catch (err) {
      setMsg(err.message);
    } finally {
      setBusy(false);
    }
  };

  const filtered = filter === 'all' ? receipts : receipts.filter((r) => (r.paymentMode || r.mode) === filter);
  const todayTotal = analytics?.opReceiptsTotal ?? 0;
  const monthTotal = monthData?.opReceiptsTotal ?? 0;

  const columns = [
    { key: 'receiptId', label: 'Receipt #', render: (r) => <span style={{ fontFamily: 'monospace', fontSize: 11, color: '#0d6e6a', fontWeight: 700 }}>{r.receiptId}</span> },
    { key: 'patientName', label: 'Patient', render: (r) => (
      <div>
        <div style={{ fontWeight: 700 }}>{r.patientName || r.patient}</div>
        {r.patientPhone && <div style={{ fontSize: 11, color: '#64748b' }}>{r.patientPhone}</div>}
        {r.patientId && <div style={{ fontSize: 11, color: '#94a3b8' }}>{r.patientId}</div>}
      </div>
    )},
    { key: 'doctor', label: 'Doctor', render: (r) => formatDoctorLabel(r.doctor) },
    { key: 'service', label: 'Service', render: (r) => <span style={{ fontSize: 12, padding: '2px 8px', borderRadius: 999, background: '#f0f9ff', color: '#0369a1', fontWeight: 600 }}>{r.service || 'Consultation'}</span> },
    { key: 'amount', label: 'Amount', render: (r) => <strong style={{ color: '#10b981', fontFamily: 'Outfit, sans-serif' }}>{formatInr(r.amount || r.total)}</strong> },
    { key: 'paymentMode', label: 'Mode', render: (r) => {
      const mode = r.paymentMode || r.mode || 'Cash';
      const colors = { Cash: '#10b981', 'UPI / GPay / PhonePe': '#6366f1', 'Debit Card': '#3b82f6', 'Credit Card': '#8b5cf6', 'Insurance / TPA': '#f97316' };
      return <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: `${colors[mode] || '#94a3b8'}15`, color: colors[mode] || '#64748b', border: `1px solid ${colors[mode] || '#94a3b8'}40` }}>{mode}</span>;
    }},
    { key: 'createdAt', label: 'Date / Time', render: (r) => {
      if (!r.createdAt) return '—';
      const d = new Date(r.createdAt);
      return <div><div style={{ fontWeight: 600 }}>{d.toLocaleDateString('en-IN')}</div><div style={{ fontSize: 11, color: '#94a3b8' }}>{d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</div></div>;
    }},
    { key: 'pdf', label: 'Print', render: (r) => (
      <button type="button" className="btn btn-sm btn-secondary" onClick={() => printOpReceipt(r, hospital, user)}>Print</button>
    )},
  ];


  return (
    <PageShell
      title="Billing & Receipts"
      subtitle="Reception billing counter · OPD · IPD · Collections · Insurance"
      actions={
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" className="btn btn-primary" onClick={() => { setShowForm((s) => !s); setTab('receipts'); }}>
            {showForm ? '✕ Close' : '+ New Bill / Receipt'}
          </button>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => { reload(); }}>Refresh</button>
        </div>
      }
    >
      {/* TOP STATS */}
      <div className="stat-grid">
        <StatCard label="Today Collected" value={formatInr(todayTotal)} accent="#10b981" icon={MdToday} loading={loading} />
        <StatCard label="Month Collected" value={formatInr(monthTotal)} accent="#059669" icon={MdCalendarMonth} loading={loading} />
        <StatCard label="Total Receipts" value={receipts.length} accent="#4f8ef7" icon={MdReceiptLong} loading={loading} />
        <StatCard label="IP Outstanding" value={formatInr(monthData?.ipOutstanding)} accent="#ef4444" icon={MdAccountBalanceWallet} loading={loading} />
      </div>

      <div className="period-tabs card mb" style={{ flexWrap: 'wrap' }}>
        <button type="button" className={`emr-chip ${tab === 'receipts' ? 'active' : ''}`} onClick={() => setTab('receipts')}>
          <MdReceiptLong style={{ verticalAlign: '-2px', marginRight: 4 }} /> OP Receipts
        </button>
        <button type="button" className={`emr-chip ${tab === 'ipdbills' ? 'active' : ''}`} onClick={() => setTab('ipdbills')}>
          <MdKingBed style={{ verticalAlign: '-2px', marginRight: 4 }} /> IPD Bills
        </button>
        <button type="button" className={`emr-chip ${tab === 'closing' ? 'active' : ''}`} onClick={() => setTab('closing')}>
          <MdSummarize style={{ verticalAlign: '-2px', marginRight: 4 }} /> Daily Closing
        </button>
        {canExpenses && (
          <button type="button" className={`emr-chip ${tab === 'expense' ? 'active' : ''}`} onClick={() => setTab('expense')}>
            <MdTrendingUp style={{ verticalAlign: '-2px', marginRight: 4 }} /> Expenses
          </button>
        )}
        <button type="button" className={`emr-chip ${tab === 'docrevenue' ? 'active' : ''}`} onClick={() => setTab('docrevenue')}>
          <MdPeople style={{ verticalAlign: '-2px', marginRight: 4 }} /> Doctor Revenue
        </button>
        <button type="button" className={`emr-chip ${tab === 'insurance' ? 'active' : ''}`} onClick={() => setTab('insurance')}>
          <MdShield style={{ verticalAlign: '-2px', marginRight: 4 }} /> Insurance / TPA
        </button>
        <button type="button" className={`emr-chip ${tab === 'packages' ? 'active' : ''}`} onClick={() => setTab('packages')}>
          <MdInventory2 style={{ verticalAlign: '-2px', marginRight: 4 }} /> Package Billing
        </button>
        <button type="button" className={`emr-chip ${tab === 'daySheet' ? 'active' : ''}`} onClick={() => setTab('daySheet')}>
          <MdDescription style={{ verticalAlign: '-2px', marginRight: 4 }} /> Day Sheet
        </button>
      </div>

      {/* RECEIPT FORM */}
      {tab === 'receipts' && showForm && (
        <form className="card mb" onSubmit={createReceipt} style={{ borderLeft: '4px solid #0d6e6a' }}>
          <h3 style={{ marginTop: 0, color: '#0d6e6a' }}>New OPD Bill / Receipt</h3>

          {/* Patient Info Row */}
          <div className="form-grid mb">
            <div className="field full">
              <label>Link to OPD queue (optional — sends patient to doctor after billing)</label>
              <select
                className="input"
                value={form.queueId}
                onChange={(e) => {
                  const qid = e.target.value;
                  const row = activeQueue.find((q) => (q.queueId || q.id) === qid);
                  setForm((f) => ({
                    ...f,
                    queueId: qid,
                    patientName: row ? (row.patientName || row.name || f.patientName) : f.patientName,
                    patientId: row?.patientId || f.patientId,
                    patientPhone: row?.patientPhone || f.patientPhone,
                    doctor: row?.doctor || f.doctor,
                  }));
                }}
              >
                <option value="">— No queue link —</option>
                {activeQueue.map((q) => {
                  const id = q.queueId || q.id;
                  return (
                    <option key={id} value={id}>
                      {(q.patientName || q.name) || 'Patient'} · {q.doctor || 'Doctor'} · {q.status} · #{q.token || id.slice(-4)}
                    </option>
                  );
                })}
              </select>
            </div>
            <div className="field"><label>Patient Name *</label><input className="input" required value={form.patientName} onChange={(e) => setForm({ ...form, patientName: e.target.value })} placeholder="Full name" /></div>
            <div className="field"><label>UHID / Patient ID</label><input className="input" value={form.patientId} onChange={(e) => setForm({ ...form, patientId: e.target.value })} placeholder="PT-XXXX" /></div>
            <div className="field"><label>Mobile Number</label><input className="input" value={form.patientPhone} onChange={(e) => setForm({ ...form, patientPhone: e.target.value })} placeholder="10-digit" /></div>
            <div className="field"><label>Doctor / Consultant</label><input className="input" value={form.doctor} onChange={(e) => setForm({ ...form, doctor: e.target.value })} placeholder="Dr. Name" /></div>
          </div>

          {/* Quick Preset Buttons */}
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 6 }}>Quick Add Preset Charges:</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {OPD_CHARGE_PRESETS.map((p) => (
                <button key={p.label} type="button"
                  style={{ fontSize: 11, padding: '4px 10px', border: '1px solid #e2e8f0', borderRadius: 6, background: '#f8fafc', cursor: 'pointer', fontWeight: 600 }}
                  onClick={() => setForm((f) => ({ ...f, lines: [...f.lines, { service: p.label, desc: '', amount: p.default }] }))}
                >
                  + {p.label} (₹{p.default})
                </button>
              ))}
            </div>
          </div>

          {/* Line Items */}
          <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden', marginBottom: 14 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f1f5f9' }}>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 700, color: '#475569' }}>Service / Item</th>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 700, color: '#475569' }}>Description</th>
                  <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 700, color: '#475569', width: 120 }}>Amount ₹</th>
                  <th style={{ width: 40 }}></th>
                </tr>
              </thead>
              <tbody>
                {form.lines.map((line, i) => (
                  <tr key={i} style={{ borderTop: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '6px 8px' }}>
                      <select className="input input-sm" value={line.service} onChange={(e) => updateLine(i, { service: e.target.value })} style={{ fontSize: 12 }}>
                        {SERVICES.map((s) => <option key={s}>{s}</option>)}
                      </select>
                    </td>
                    <td style={{ padding: '6px 8px' }}>
                      <input className="input input-sm" value={line.desc} onChange={(e) => updateLine(i, { desc: e.target.value })} placeholder="Notes (optional)" style={{ fontSize: 12 }} />
                    </td>
                    <td style={{ padding: '6px 8px' }}>
                      <input className="input input-sm" type="number" min={0} value={line.amount} onChange={(e) => updateLine(i, { amount: e.target.value })} style={{ textAlign: 'right', fontSize: 13, fontWeight: 700 }} />
                    </td>
                    <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                      {form.lines.length > 1 && <button type="button" style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 16 }} onClick={() => removeLine(i)}>×</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ background: '#f8fafc', borderTop: '2px solid #e2e8f0' }}>
                  <td colSpan={2} style={{ padding: '8px 10px', fontWeight: 700, color: '#475569' }}>
                    <button type="button" className="btn btn-sm btn-secondary" onClick={addLine} style={{ fontSize: 11 }}>+ Add Line</button>
                  </td>
                  <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 900, fontSize: 16, color: '#0f172a' }}>₹{lineTotal.toLocaleString('en-IN')}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Payment Row */}
          <div className="form-grid mb">
            <div className="field">
              <label>Payment Mode *</label>
              <select className="input" value={form.paymentMode} onChange={(e) => setForm({ ...form, paymentMode: e.target.value })}>
                {PAYMENT_MODES.map((m) => <option key={m}>{m}</option>)}
              </select>
            </div>
            <div className="field">
              <label>GST % (tax invoice)</label>
              <select className="input" value={form.gstPct} onChange={(e) => setForm({ ...form, gstPct: Number(e.target.value) })}>
                <option value={0}>0% — no GST</option>
                <option value={5}>5% GST</option>
                <option value={12}>12% GST</option>
                <option value={18}>18% GST</option>
              </select>
            </div>
            <div className="field">
              <label>Transaction Ref / UPI ID / Cheque No.</label>
              <input className="input" value={form.txnRef} onChange={(e) => setForm({ ...form, txnRef: e.target.value })} placeholder="UPI ref / TXN ID / Cheque No." />
            </div>
            <div className="field full">
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input type="checkbox" checked={!!form.splitPay} onChange={(e) => setForm({ ...form, splitPay: e.target.checked })} />
                Split payment (Cash + UPI / Card)
              </label>
            </div>
            {form.splitPay && (
              <>
                <div className="field">
                  <label>Second mode</label>
                  <select className="input" value={form.pay2Mode} onChange={(e) => setForm({ ...form, pay2Mode: e.target.value })}>
                    {PAYMENT_MODES.map((m) => <option key={m}>{m}</option>)}
                  </select>
                </div>
                <div className="field">
                  <label>Amount in second mode ₹</label>
                  <input className="input" type="number" min={0} value={form.pay2Amount} onChange={(e) => setForm({ ...form, pay2Amount: e.target.value })} placeholder="e.g. 200" />
                </div>
              </>
            )}
            <div className="field full">
              <label>Notes (optional)</label>
              <input className="input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Any additional notes" />
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderTop: '1px solid #e2e8f0' }}>
            <div style={{ fontSize: 13, color: '#64748b' }}>Total: <strong style={{ fontSize: 20, color: '#0f172a', fontFamily: 'Outfit, sans-serif' }}>₹{lineTotal.toLocaleString('en-IN')}</strong> via <strong>{form.paymentMode}</strong></div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? 'Saving…' : '✓ Save & Print Receipt'}</button>
            </div>
          </div>
        </form>
      )}

      {msg && <div className={`mb ${msg.startsWith('✓') ? 'info-banner' : 'error'}`} style={{ fontWeight: 600 }}>{msg} <button type="button" style={{ marginLeft: 8, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 900 }} onClick={() => setMsg('')}>✕</button></div>}
      {error && <div className="error mb">{error}</div>}

      {tab === 'receipts' && (
        <>
          <div className="row-actions mb" style={{ flexWrap: 'wrap', gap: '0.5rem' }}>
            <span className="hint">Filter by mode:</span>
            <button type="button" className={`emr-chip ${filter === 'all' ? 'active' : ''}`} onClick={() => setFilter('all')}>All ({receipts.length})</button>
            {['Cash', 'UPI / GPay / PhonePe', 'Debit Card', 'Credit Card', 'Insurance / TPA'].map((m) => {
              const count = receipts.filter((r) => (r.paymentMode || r.mode || 'Cash') === m).length;
              return count > 0 ? (
                <button key={m} type="button" className={`emr-chip ${filter === m ? 'active' : ''}`} onClick={() => setFilter(m)}>{m} ({count})</button>
              ) : null;
            })}
          </div>
          <DataTable columns={columns} rows={filtered} keyField="receiptId" loading={loading} emptyMessage={filter === 'all' ? 'No receipts yet. Create one with "New receipt".' : `No ${filter} receipts.`} />
        </>
      )}

      {tab === 'ipdbills' && <IpdBillsTab hospital={hospital} user={user} />}
      {tab === 'insurance' && <InsuranceTab />}
      {tab === 'closing' && <DailyClosingTab receipts={receipts} todayTotal={todayTotal} />}
      {tab === 'expense' && canExpenses && <ExpenseTab />}
      {tab === 'docrevenue' && <DoctorRevenueTab receipts={receipts} />}
      {tab === 'packages' && <PackageBillingTab />}
      {tab === 'daySheet' && <DaySheetTab />}
    </PageShell>
  );
}

/* IPD Bills Tab — Full Receptionist Billing Station */
const CHARGE_CATS_RECEP = [
  'Room / Bed Charges', 'Doctor Visit / Consultation', 'Nursing Charges',
  'Investigation / Lab', 'Radiology / X-Ray / ECG', 'Pharmacy / Medicines',
  'Operation Theatre (OT)', 'Anaesthesia', 'Procedure / Dressing',
  'ICU Charges', 'Oxygen / Ventilator', 'Blood / Blood Products',
  'Consumables / Disposables', 'Ambulance', 'Canteen / Dietary',
  'Advance Payment', 'Other Charges',
];

function IpdBillsTab({ hospital, user }) {
  const { data, loading, error, reload } = useLiveData(() => ipApi.getAdmissions(100), []);
  const admissions = (data?.admissions || []);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('active');
  const [selectedAdm, setSelectedAdm] = useState(null);
  const [msg, setMsg] = useState('');

  // When data reloads, refresh selected patient data too
  const selectedFresh = selectedAdm
    ? admissions.find((a) => a.admissionId === selectedAdm.admissionId) || selectedAdm
    : null;

  const filtered = admissions.filter((a) => {
    const q = search.toLowerCase();
    const matchSearch = !search
      || (a.patientName || '').toLowerCase().includes(q)
      || (a.admissionId || '').toLowerCase().includes(q)
      || (a.patientId || '').toLowerCase().includes(q)
      || (a.doctor || '').toLowerCase().includes(q);
    const matchStatus = filterStatus === 'all'
      || (filterStatus === 'active' ? a.status !== 'Discharged' : a.status === 'Discharged');
    return matchSearch && matchStatus;
  });

  const totalOutstanding = admissions.filter((a) => a.status !== 'Discharged').reduce((s, a) => (
    s + Math.max(0, Number(a.pendingBill || 0))
  ), 0);
  const totalAdvance = admissions.reduce((s, a) => s + Number(a.advanceAmount || 0), 0);
  const totalCollected = admissions.reduce((s, a) => {
    const pays = (a.charges || [])
      .filter((c) => c.category === 'Payment Received')
      .reduce((x, c) => x + Math.abs(Number(c.amount ?? (Number(c.qty || 1) * Number(c.rate || 0)))), 0);
    return s + Number(a.advanceAmount || 0) + pays;
  }, 0);

  if (selectedFresh) {
    return (
      <PatientBillingStation
        adm={selectedFresh}
        hospital={hospital}
        user={user}
        onBack={() => { setSelectedAdm(null); reload(); }}
        onMsg={setMsg}
      />
    );
  }

  return (
    <div>
      {msg && <div className={`mb ${/fail|error/i.test(msg) ? 'error' : 'info-banner'}`}>{msg}<button type="button" style={{ marginLeft: 10, fontWeight: 700 }} onClick={() => setMsg('')}>✕</button></div>}

      {/* Summary bar */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10, marginBottom: 14 }}>
        {[
          { label: 'Active IP Patients', value: admissions.filter(a => a.status !== 'Discharged').length, color: '#f97316', sub: 'currently admitted' },
          { label: 'Total Outstanding', value: `₹${totalOutstanding.toLocaleString('en-IN')}`, color: '#ef4444', sub: 'balance due from active' },
          { label: 'Total Advance Collected', value: `₹${totalAdvance.toLocaleString('en-IN')}`, color: '#10b981', sub: 'advance received' },
          { label: 'Total Collected (All)', value: `₹${totalCollected.toLocaleString('en-IN')}`, color: '#3b82f6', sub: 'advance + payments' },
        ].map(({ label, value, color, sub }) => (
          <div key={label} className="card" style={{ textAlign: 'center', padding: 14, borderTop: `3px solid ${color}` }}>
            <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase' }}>{label}</div>
            <div style={{ fontFamily: 'Outfit, sans-serif', fontSize: 22, fontWeight: 900, color }}>{value}</div>
            <div style={{ fontSize: 10.5, color: '#94a3b8' }}>{sub}</div>
          </div>
        ))}
      </div>

      {/* Search + Filter */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <input className="input" style={{ maxWidth: 340 }} placeholder="Search patient name / IP ID / UHID / Doctor..." value={search} onChange={(e) => setSearch(e.target.value)} />
        {['active', 'discharged', 'all'].map((s) => (
          <button key={s} type="button" className={`emr-chip ${filterStatus === s ? 'active' : ''}`} onClick={() => setFilterStatus(s)}>
            {s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
        <span style={{ marginLeft: 'auto', fontSize: 12, color: '#94a3b8' }}>Click any row to open billing</span>
      </div>

      {error && <div className="error mb">{error}</div>}
      {loading && <p className="hint">Loading...</p>}

      {/* Patient list table */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f1f5f9' }}>
              {['IP ID', 'Patient', 'UHID', 'Doctor', 'Ward/Bed', 'Admit Date', 'Total Bill', 'Advance', 'Collections', 'Balance Due', 'Status', 'Action'].map((h) => (
                <th key={h} style={{ padding: '9px 10px', border: '1px solid #e2e8f0', fontWeight: 700, color: '#475569', whiteSpace: 'nowrap', fontSize: 11, textAlign: h === 'Total Bill' || h === 'Advance' || h === 'Collections' || h === 'Balance Due' ? 'right' : 'left' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((a, i) => {
              const advance = Number(a.advanceAmount || 0);
              // pendingBill already includes daily room accrual from the scheduler — do not add (days-1)*rate again
              const balance = Math.max(0, Number(a.pendingBill || 0));
              const paymentAmt = (a.charges || [])
                .filter((c) => c.category === 'Payment Received')
                .reduce((s, c) => s + Math.abs(Number(c.amount ?? (Number(c.qty || 1) * Number(c.rate || 0)))), 0);
              const totalColl = advance + paymentAmt;
              const total = balance + totalColl;
              const isDischarged = a.status === 'Discharged';
              return (
                <tr
                  key={a.admissionId}
                  style={{ background: i % 2 === 0 ? '#fff' : '#fafbfc', cursor: 'pointer' }}
                  onClick={() => setSelectedAdm(a)}
                >
                  <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9', fontFamily: 'monospace', fontSize: 11, color: '#0d6e6a', fontWeight: 700 }}>{a.admissionId}</td>
                  <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9', fontWeight: 700 }}>
                    {a.patientName}
                    {a.age && <div style={{ fontSize: 11, color: '#64748b' }}>{a.age}y / {a.gender || '—'}</div>}
                  </td>
                  <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9', fontSize: 11, color: '#64748b' }}>{a.patientId || '—'}</td>
                  <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9', fontSize: 12 }}>Dr. {a.doctor || '—'}</td>
                  <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9', fontSize: 11 }}>{a.ward} · {a.bedLabel || a.bedId}</td>
                  <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9', fontSize: 11, whiteSpace: 'nowrap' }}>{a.admittedAt ? new Date(a.admittedAt).toLocaleDateString('en-IN') : '—'}</td>
                  <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9', textAlign: 'right', fontWeight: 700 }}>₹{total.toLocaleString('en-IN')}</td>
                  <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9', textAlign: 'right', color: '#10b981', fontWeight: 600 }}>₹{advance.toLocaleString('en-IN')}</td>
                  <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9', textAlign: 'right', color: '#3b82f6', fontWeight: 600 }}>₹{paymentAmt.toLocaleString('en-IN')}</td>
                  <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9', textAlign: 'right', fontWeight: 800, color: balance > 0 ? '#ef4444' : '#10b981' }}>
                    {balance > 0 ? `₹${balance.toLocaleString('en-IN')}` : '✓ Paid'}
                  </td>
                  <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9' }}>
                    <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: isDischarged ? '#f1f5f9' : '#dcfce7', color: isDischarged ? '#64748b' : '#16a34a' }}>
                      {a.status || 'Admitted'}
                    </span>
                  </td>
                  <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9' }}>
                    <button type="button" className="btn btn-sm btn-primary" style={{ fontSize: 11 }} onClick={(e) => { e.stopPropagation(); setSelectedAdm(a); }}>
                      Open Billing
                    </button>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && !loading && (
              <tr><td colSpan={12} style={{ padding: 24, textAlign: 'center', color: '#94a3b8', border: '1px solid #f1f5f9' }}>No IPD bills found. Admit a patient first.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* Patient Billing Station (full detail page for one patient) */
function PatientBillingStation({ adm: admProp, hospital, user, onBack, onMsg }) {
  const [chargeForm, setChargeForm] = useState({ category: 'Doctor Visit / Consultation', desc: '', qty: 1, rate: '' });
  const [payForm, setPayForm] = useState({ amount: '', mode: 'Cash', remarks: '', receiptFor: 'Advance / Interim Payment' });
  const [section, setSection] = useState('bill'); // 'bill' | 'addcharge' | 'pay'
  const [busy, setBusy] = useState(false);
  const [payBusy, setPayBusy] = useState(false);
  const { data: admData, reload } = useLiveData(() => ipApi.getAdmission(admProp.admissionId), [admProp.admissionId]);
  // Prefer freshly loaded admission so the bill updates after each charge/payment
  const adm = admData?.admission || admProp;

  const charges = adm.charges || [];
  const advance = Number(adm.advanceAmount || 0);
  const ratePerDay = Number(adm.ratePerDay || 0);
  const daysAdmitted = Math.max(1, Math.floor((Date.now() - new Date(adm.admittedAt || Date.now()).getTime()) / 86400000) || 1);
  const roomCharge = ratePerDay * daysAdmitted;
  const extrasGross = charges
    .filter((c) => c.category !== 'Payment Received' && c.category !== 'Advance')
    .reduce((s, c) => s + Math.max(0, Number(c.amount ?? (Number(c.qty || 1) * Number(c.rate || 0)))), 0);
  const paymentsAmt = charges
    .filter((c) => c.category === 'Payment Received')
    .reduce((s, c) => s + Math.abs(Number(c.amount ?? (Number(c.qty || 1) * Number(c.rate || 0)))), 0);
  // Gross bill (multi-day room + extras). Advance is collection, not a charge reduction twice.
  const total = roomCharge + extrasGross;
  const totalCollected = advance + paymentsAmt;
  const balanceDue = Math.max(0, total - totalCollected);
  const excess = Math.max(0, totalCollected - total);

  const addCharge = async () => {
    if (!chargeForm.desc.trim() || !(Number(chargeForm.rate) > 0)) { onMsg('Enter description and rate.'); return; }
    setBusy(true);
    try {
      await ipApi.addAdmissionCharge(adm.admissionId, { ...chargeForm, qty: Number(chargeForm.qty), rate: Number(chargeForm.rate) });
      setChargeForm({ category: 'Doctor Visit / Consultation', desc: '', qty: 1, rate: '' });
      setSection('bill');
      await reload();
      onMsg('Charge added to bill.');
    } catch (e) { onMsg(e.message); } finally { setBusy(false); }
  };

  const collectPayment = async () => {
    if (!(Number(payForm.amount) > 0)) { onMsg('Enter a valid payment amount.'); return; }
    setPayBusy(true);
    try {
      const amount = Number(payForm.amount);
      await ipApi.addAdmissionCharge(adm.admissionId, {
        category: 'Payment Received',
        desc: `${payForm.receiptFor} — ${payForm.mode}${payForm.remarks ? ` (Ref: ${payForm.remarks})` : ''}`,
        qty: 1,
        rate: -amount,
      });
      let receiptData;
      try {
        const created = await ipApi.createOpReceipt({
          type: 'IP',
          admissionId: adm.admissionId,
          patientId: adm.patientId || adm.admissionId,
          patientName: adm.patientName,
          patientPhone: adm.phone || '',
          amount,
          paymentMode: payForm.mode,
          service: payForm.receiptFor || 'IP Payment',
          notes: payForm.remarks || '',
          doctor: adm.doctor || '',
          txnRef: payForm.remarks || '',
        });
        receiptData = {
          ...(created?.receipt || {}),
          receiptId: created?.receipt?.receiptId || `NET-${Date.now()}`,
          patientName: adm.patientName,
          patientId: adm.patientId || adm.admissionId,
          amount,
          paymentMode: payForm.mode,
          service: payForm.receiptFor,
          remarks: payForm.remarks,
          createdAt: new Date().toISOString(),
          admissionId: adm.admissionId,
          doctor: adm.doctor,
          ward: adm.ward,
        };
      } catch (receiptErr) {
        // Charge already saved — still print local copy if ledger receipt fails
        receiptData = {
          receiptId: `RCP-${Date.now().toString(36).toUpperCase()}`,
          patientName: adm.patientName,
          patientId: adm.patientId || adm.admissionId,
          amount,
          paymentMode: payForm.mode,
          service: payForm.receiptFor,
          remarks: payForm.remarks,
          createdAt: new Date().toISOString(),
          admissionId: adm.admissionId,
          doctor: adm.doctor,
          ward: adm.ward,
        };
        onMsg(`Payment on bill OK; receipt ledger: ${receiptErr.message}`);
      }
      printOpReceipt(receiptData, hospital, user);
      setPayForm({ amount: '', mode: 'Cash', remarks: '', receiptFor: 'Advance / Interim Payment' });
      setSection('bill');
      await reload();
      onMsg('Payment recorded and receipt printed.');
    } catch (e) { onMsg(e.message); } finally { setPayBusy(false); }
  };

  const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, padding: '12px 16px', background: '#f8fafc', borderRadius: 12, border: '1px solid #e2e8f0' }}>
        <button type="button" className="btn btn-secondary btn-sm" onClick={onBack}>← All Patients</button>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 900, fontSize: 17 }}>{adm.patientName}</div>
          <div style={{ fontSize: 12, color: '#64748b' }}>
            {adm.age && `${adm.age}y / ${adm.gender || '—'} · `}
            IP: <strong>{adm.admissionId}</strong> · UHID: {adm.patientId || '—'} · Dr. {adm.doctor || '—'} · {adm.ward} — {adm.bedLabel || adm.bedId}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <a href={`/ipd/patient/${adm.admissionId}`} className="btn btn-sm btn-secondary" style={{ textDecoration: 'none', fontSize: 12 }}>Full Chart</a>
          <button type="button" className="btn btn-sm btn-secondary" style={{ fontSize: 12 }} onClick={() => window.print()}>Print Bill</button>
        </div>
      </div>

      {/* Financial Summary */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginBottom: 14 }}>
        {[
          { label: 'Days Admitted', value: `${daysAdmitted} days`, color: '#6366f1', sub: `Admit: ${adm.admittedAt ? new Date(adm.admittedAt).toLocaleDateString('en-IN') : '—'}` },
          { label: 'Total Bill', value: inr(total), color: '#0f172a', sub: 'gross charges' },
          { label: 'Advance Paid', value: inr(advance), color: '#10b981', sub: adm.advanceMode || 'Cash' },
          { label: 'Collections', value: inr(paymentsAmt), color: '#3b82f6', sub: `${charges.filter((c) => c.category === 'Payment Received').length} payment(s)` },
          { label: 'Total Collected', value: inr(totalCollected), color: '#059669', sub: 'advance + payments' },
          { label: excess > 0 ? 'Refund Due' : 'Balance Due', value: excess > 0 ? inr(excess) : inr(balanceDue), color: balanceDue > 0 ? '#ef4444' : '#10b981', sub: balanceDue > 0 ? 'Pending' : '✓ Settled' },
        ].map(({ label, value, color, sub }) => (
          <div key={label} className="card" style={{ textAlign: 'center', padding: 12, borderTop: `3px solid ${color}` }}>
            <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
            <div style={{ fontFamily: 'Outfit, sans-serif', fontSize: 18, fontWeight: 900, color, marginTop: 2 }}>{value}</div>
            <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>{sub}</div>
          </div>
        ))}
      </div>

      {/* Action buttons */}
      {adm.status !== 'Discharged' && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
          <button type="button" className={`btn ${section === 'addcharge' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setSection(section === 'addcharge' ? 'bill' : 'addcharge')}>
            + Add Charge / Service
          </button>
          <button type="button" className={`btn ${section === 'pay' ? 'btn-primary' : 'btn-secondary'}`} style={{ background: section === 'pay' ? '#10b981' : '', borderColor: '#10b981', color: section === 'pay' ? '#fff' : '#10b981' }} onClick={() => setSection(section === 'pay' ? 'bill' : 'pay')}>
            Collect Payment / Advance
          </button>
        </div>
      )}

      {/* Add Charge Form */}
      {section === 'addcharge' && adm.status !== 'Discharged' && (
        <div className="card mb" style={{ borderLeft: '4px solid #6366f1', marginBottom: 16 }}>
          <h3 style={{ marginTop: 0, color: '#6366f1' }}>+ Add Service / Charge to Bill</h3>
          <div className="form-grid mb">
            <div className="field">
              <label>Category</label>
              <select className="input" value={chargeForm.category} onChange={(e) => setChargeForm({ ...chargeForm, category: e.target.value })}>
                {CHARGE_CATS_RECEP.map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div className="field">
              <label>Description *</label>
              <input className="input" value={chargeForm.desc} onChange={(e) => setChargeForm({ ...chargeForm, desc: e.target.value })} placeholder="e.g. CBC Test, ECG, Dressing, Injection, Room Rent..." />
            </div>
            <div className="field">
              <label>Quantity</label>
              <input className="input" type="number" min={1} value={chargeForm.qty} onChange={(e) => setChargeForm({ ...chargeForm, qty: e.target.value })} />
            </div>
            <div className="field">
              <label>Rate ₹ (per unit) *</label>
              <input className="input" type="number" min={0} value={chargeForm.rate} onChange={(e) => setChargeForm({ ...chargeForm, rate: e.target.value })} placeholder="Amount" />
            </div>
            {chargeForm.rate && chargeForm.qty && (
              <div className="field full">
                <div className="info-banner">
                  Line Total: <strong style={{ fontSize: 16 }}>{inr(Number(chargeForm.qty) * Number(chargeForm.rate))}</strong>
                </div>
              </div>
            )}
          </div>
          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setSection('bill')}>Cancel</button>
            <button type="button" className="btn btn-primary" disabled={busy} onClick={addCharge}>{busy ? 'Adding…' : '✓ Add to Bill'}</button>
          </div>
        </div>
      )}

      {/* Collect Payment Form */}
      {section === 'pay' && adm.status !== 'Discharged' && (
        <div className="card mb" style={{ borderLeft: '4px solid #10b981', marginBottom: 16 }}>
          <h3 style={{ marginTop: 0, color: '#10b981' }}>Collect Payment from Patient</h3>
          {balanceDue > 0 && (
            <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 13, color: '#dc2626', fontWeight: 600 }}>
              Balance Due: <strong>{inr(balanceDue)}</strong> — collect full or partial amount
            </div>
          )}
          <div className="form-grid mb">
            <div className="field">
              <label>Payment For</label>
              <select className="input" value={payForm.receiptFor} onChange={(e) => setPayForm({ ...payForm, receiptFor: e.target.value })}>
                {['Advance / Interim Payment', 'Against Bill', 'Full & Final Settlement', 'Partial Payment', 'Refund'].map((t) => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div className="field">
              <label>Amount ₹ *</label>
              <input className="input" type="number" min={1} value={payForm.amount}
                onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })}
                placeholder={balanceDue > 0 ? `Balance: ₹${balanceDue.toLocaleString('en-IN')}` : 'Enter amount'} />
            </div>
            <div className="field">
              <label>Payment Mode</label>
              <select className="input" value={payForm.mode} onChange={(e) => setPayForm({ ...payForm, mode: e.target.value })}>
                {['Cash', 'UPI / GPay / PhonePe', 'Debit Card', 'Credit Card', 'NEFT / Bank Transfer', 'Cheque', 'Insurance / TPA', 'DD (Demand Draft)'].map((m) => <option key={m}>{m}</option>)}
              </select>
            </div>
            <div className="field">
              <label>Transaction Ref / Cheque No.</label>
              <input className="input" value={payForm.remarks} onChange={(e) => setPayForm({ ...payForm, remarks: e.target.value })} placeholder="UPI ref / Cheque no. / TXN ID" />
            </div>
          </div>
          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setSection('bill')}>Cancel</button>
            <button type="button" className="btn btn-primary" style={{ background: '#10b981', borderColor: '#10b981' }} disabled={payBusy} onClick={collectPayment}>
              {payBusy ? 'Processing…' : '✓ Record Payment & Print Receipt'}
            </button>
          </div>
        </div>
      )}

      {/* Itemized Bill */}
      <div className="card">
        <h3 style={{ marginTop: 0 }}>Itemized Bill</h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                {['#', 'Date / Time', 'Category', 'Description', 'Qty', 'Rate', 'Amount'].map((h) => (
                  <th key={h} style={{ padding: '9px 10px', border: '1px solid #e2e8f0', fontWeight: 700, color: '#475569', textAlign: ['Qty', 'Rate', 'Amount'].includes(h) ? 'right' : 'left' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {/* Advance row */}
              {advance > 0 && (
                <tr style={{ background: '#f0fdf4' }}>
                  <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9', color: '#94a3b8' }}>—</td>
                  <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9', fontSize: 11.5, color: '#64748b' }}>{adm.admittedAt ? new Date(adm.admittedAt).toLocaleDateString('en-IN') : '—'}</td>
                  <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9' }}><span style={{ fontSize: 11, fontWeight: 700, background: '#dcfce7', color: '#16a34a', padding: '2px 8px', borderRadius: 999 }}>Advance</span></td>
                  <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9', color: '#16a34a', fontStyle: 'italic' }}>Advance payment at admission — {adm.advanceMode || 'Cash'}</td>
                  <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9', textAlign: 'right' }}>1</td>
                  <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9', textAlign: 'right', color: '#16a34a' }}>-{inr(advance)}</td>
                  <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9', textAlign: 'right', fontWeight: 700, color: '#16a34a' }}>-{inr(advance)}</td>
                </tr>
              )}
              {/* Room charges */}
              <tr>
                <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9', color: '#94a3b8' }}>1</td>
                <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9', fontSize: 11.5, color: '#64748b' }}>{adm.admittedAt ? new Date(adm.admittedAt).toLocaleDateString('en-IN') : '—'}</td>
                <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9' }}>Room / Bed</td>
                <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9' }}>{adm.ward} — {adm.bedLabel || adm.bedId} (Bed + Nursing charges)</td>
                <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9', textAlign: 'right' }}>{daysAdmitted} day(s)</td>
                <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9', textAlign: 'right' }}>{inr(adm.ratePerDay || 0)}/day</td>
                <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9', textAlign: 'right', fontWeight: 700 }}>{inr(Math.max(0, (adm.ratePerDay || 0) * daysAdmitted))}</td>
              </tr>
              {/* All charges */}
              {charges.filter((c) => c.category !== 'Payment Received').map((c, i) => (
                <tr key={c.chargeId || i} style={{ background: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
                  <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9', color: '#94a3b8' }}>{i + 2}</td>
                  <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9', fontSize: 11.5, color: '#64748b' }}>{c.at ? new Date(c.at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                  <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9', fontSize: 12 }}>{c.category}</td>
                  <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9', fontWeight: 600 }}>{c.desc}</td>
                  <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9', textAlign: 'right' }}>{c.qty}</td>
                  <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9', textAlign: 'right' }}>{inr(c.rate)}</td>
                  <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9', textAlign: 'right', fontWeight: 700 }}>{inr(c.amount || (c.qty * c.rate))}</td>
                </tr>
              ))}
              {/* Payment rows */}
              {charges.filter((c) => c.category === 'Payment Received').map((c, i) => (
                <tr key={`pay-${i}`} style={{ background: '#f0fdf4' }}>
                  <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9', color: '#94a3b8' }}>—</td>
                  <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9', fontSize: 11.5, color: '#64748b' }}>{c.at ? new Date(c.at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                  <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9' }}><span style={{ fontSize: 11, fontWeight: 700, background: '#dcfce7', color: '#16a34a', padding: '2px 8px', borderRadius: 999 }}>Payment</span></td>
                  <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9', color: '#16a34a', fontStyle: 'italic' }}>{c.desc}</td>
                  <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9', textAlign: 'right' }}>1</td>
                  <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9', textAlign: 'right', color: '#16a34a' }}>{inr(Math.abs(c.rate))}</td>
                  <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9', textAlign: 'right', fontWeight: 700, color: '#16a34a' }}>-{inr(Math.abs(c.amount || c.rate))}</td>
                </tr>
              ))}
              {/* Totals */}
              <tr style={{ background: '#f8fafc', fontWeight: 800 }}>
                <td colSpan={6} style={{ padding: '10px 10px', border: '1px solid #e2e8f0', textAlign: 'right', fontWeight: 700, color: '#475569' }}>Gross Total</td>
                <td style={{ padding: '10px 10px', border: '1px solid #e2e8f0', textAlign: 'right', fontWeight: 900, fontSize: 15, color: '#0f172a' }}>{inr(total)}</td>
              </tr>
              <tr style={{ background: '#f0fdf4' }}>
                <td colSpan={6} style={{ padding: '8px 10px', border: '1px solid #e2e8f0', textAlign: 'right', color: '#16a34a', fontWeight: 700 }}>Total Collected (Advance + Payments)</td>
                <td style={{ padding: '8px 10px', border: '1px solid #e2e8f0', textAlign: 'right', fontWeight: 900, fontSize: 15, color: '#16a34a' }}>-{inr(totalCollected)}</td>
              </tr>
              <tr style={{ background: balanceDue > 0 ? '#fef2f2' : '#f0fdf4' }}>
                <td colSpan={6} style={{ padding: '10px 10px', border: '1px solid #e2e8f0', textAlign: 'right', fontWeight: 800, fontSize: 14, color: balanceDue > 0 ? '#dc2626' : '#16a34a' }}>
                  {excess > 0 ? 'Excess / Refund Due to Patient' : balanceDue > 0 ? 'Balance Due from Patient' : 'Bill Fully Settled'}
                </td>
                <td style={{ padding: '10px 10px', border: '1px solid #e2e8f0', textAlign: 'right', fontWeight: 900, fontSize: 20, color: balanceDue > 0 ? '#dc2626' : '#16a34a' }}>
                  {excess > 0 ? inr(excess) : inr(balanceDue)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        {adm.status !== 'Discharged' && balanceDue > 0 && (
          <div style={{ marginTop: 12, padding: '10px 14px', background: '#fef2f2', borderRadius: 10, border: '1px solid #fca5a5', fontSize: 13, color: '#dc2626', fontWeight: 600 }}>
            ₹{balanceDue.toLocaleString('en-IN')} still pending. Collect before discharge.
            <button type="button" className="btn btn-sm btn-primary" style={{ marginLeft: 12, background: '#10b981', borderColor: '#10b981' }} onClick={() => setSection('pay')}>
              Collect Now
            </button>
          </div>
        )}
      </div>

      {/* Payment History */}
      {(paymentsAmt > 0 || advance > 0) && (
        <div className="card" style={{ marginTop: 14 }}>
          <h3 style={{ marginTop: 0 }}>Payment Receipt History</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                {['#', 'Date', 'Description', 'Mode', 'Amount', 'Print'].map((h) => (
                  <th key={h} style={{ padding: '9px 10px', border: '1px solid #e2e8f0', fontWeight: 700, color: '#475569', textAlign: h === 'Amount' ? 'right' : 'left' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {advance > 0 && (
                <tr style={{ background: '#f0fdf4' }}>
                  <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9' }}>1</td>
                  <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9', fontSize: 11.5 }}>{adm.admittedAt ? new Date(adm.admittedAt).toLocaleDateString('en-IN') : '—'}</td>
                  <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9' }}>Advance at Admission</td>
                  <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9' }}>{adm.advanceMode || 'Cash'}</td>
                  <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9', textAlign: 'right', fontWeight: 700, color: '#10b981' }}>{inr(advance)}</td>
                  <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9' }}>
                    <button type="button" className="btn btn-sm btn-secondary" style={{ fontSize: 11 }}
                      onClick={() => printOpReceipt({ receiptId: `ADV-${adm.admissionId}`, patientName: adm.patientName, patientId: adm.patientId, amount: advance, paymentMode: adm.advanceMode || 'Cash', service: 'Advance at Admission', createdAt: adm.admittedAt, admissionId: adm.admissionId }, hospital, user)}>
                      Print
                    </button>
                  </td>
                </tr>
              )}
              {(adm.charges || []).filter(c => c.category === 'Payment Received').map((c, i) => (
                <tr key={i}>
                  <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9' }}>{i + 2}</td>
                  <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9', fontSize: 11.5 }}>{c.at ? new Date(c.at).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                  <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9' }}>{c.desc}</td>
                  <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9' }}>—</td>
                  <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9', textAlign: 'right', fontWeight: 700, color: '#10b981' }}>{inr(Math.abs(c.amount || c.rate))}</td>
                  <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9' }}>
                    <button type="button" className="btn btn-sm btn-secondary" style={{ fontSize: 11 }}
                      onClick={() => printOpReceipt({ receiptId: c.chargeId || `PAY-${i}`, patientName: adm.patientName, patientId: adm.patientId, amount: Math.abs(c.amount || c.rate), paymentMode: 'Cash', service: c.desc, createdAt: c.at, admissionId: adm.admissionId }, hospital, user)}>
                      Print
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* Daily Closing Report Tab */
function DailyClosingTab({ receipts, todayTotal }) {
  const { data: expenseData } = useLiveData(() => ipApi.getExpenses(80), []);
  const todayIso = new Date().toISOString().slice(0, 10);
  const todayExpenses = (expenseData?.expenses || []).filter((e) => (e.date || '').slice(0, 10) === todayIso);
  const expenseTotal = todayExpenses.reduce((s, e) => s + Number(e.amount || 0), 0);

  const today = new Date().toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  const todayReceipts = receipts.filter((r) => {
    const d = new Date(r.createdAt || '');
    const now = new Date();
    return d.toDateString() === now.toDateString() && String(r.type || 'OP').toUpperCase() !== 'IP';
  });
  const todayIpReceipts = receipts.filter((r) => {
    const d = new Date(r.createdAt || '');
    const now = new Date();
    return d.toDateString() === now.toDateString() && String(r.type || '').toUpperCase() === 'IP';
  });
  const ipClosingTotal = todayIpReceipts.reduce((s, r) => s + Number(r.amount || r.total || 0), 0);

  const byMode = ['Cash', 'UPI', 'Card', 'Insurance', 'Credit'].map((mode) => {
    // Receipts store variants like "UPI / GPay", "Debit Card" — match on prefix/keyword
    const matches = (raw) => {
      const m = String(raw || 'Cash').toLowerCase();
      if (mode === 'Cash') return m.startsWith('cash');
      if (mode === 'UPI') return m.includes('upi') || m.includes('gpay') || m.includes('phonepe');
      if (mode === 'Card') return m.includes('card');
      if (mode === 'Insurance') return m.includes('insurance') || m.includes('tpa');
      if (mode === 'Credit') return m.includes('credit') && !m.includes('card');
      return false;
    };
    const mReceipts = todayReceipts.filter((r) => matches(r.paymentMode || r.mode));
    return { mode, count: mReceipts.length, total: mReceipts.reduce((s, r) => s + Number(r.amount || r.total || 0), 0) };
  }).filter((m) => m.count > 0);

  const byService = ['Consultation', 'Procedure', 'Lab', 'Pharmacy', 'IP Advance', 'Other'].map((svc) => {
    const key = svc.toLowerCase();
    const sReceipts = todayReceipts.filter((r) => {
      const s = String(r.service || '').toLowerCase();
      if (key === 'consultation') return s.includes('consult') || s.includes('opd');
      if (key === 'procedure') return s.includes('procedure') || s.includes('ot');
      if (key === 'lab') return s.includes('lab') || s.includes('investigat');
      if (key === 'pharmacy') return s.includes('pharm') || s.includes('medicine');
      if (key === 'ip advance') return s.includes('advance') || s.includes('ip ');
      return s && !['consult', 'opd', 'procedure', 'ot', 'lab', 'pharm', 'medicine', 'advance'].some((k) => s.includes(k));
    });
    return { svc, count: sReceipts.length, total: sReceipts.reduce((s, r) => s + Number(r.amount || r.total || 0), 0) };
  }).filter((s) => s.count > 0);

  const grandTotal = todayReceipts.reduce((s, r) => s + Number(r.amount || r.total || 0), 0);

  return (
    <div>
      <div className="closing-report-header">
        <div className="closing-report-date">{today}</div>
        <button type="button" className="btn btn-secondary" onClick={() => window.print()}><MdPrint /> Print Report</button>
      </div>

      <div className="closing-report-grid">
        <div className="closing-section">
          <div className="closing-section-title">Collection by Payment Mode</div>
          {byMode.length === 0 ? <p className="hint">No transactions today.</p> : byMode.map(({ mode, count, total }) => (
            <div key={mode} className="closing-row">
              <span className="closing-row-label">{mode}</span>
              <span className="closing-row-count">{count} txn{count > 1 ? 's' : ''}</span>
              <span className="closing-row-total" style={{ color: '#10b981' }}>₹{total.toLocaleString('en-IN')}</span>
            </div>
          ))}
        </div>
        <div className="closing-section">
          <div className="closing-section-title">Collection by Service</div>
          {byService.length === 0 ? <p className="hint">No transactions today.</p> : byService.map(({ svc, count, total }) => (
            <div key={svc} className="closing-row">
              <span className="closing-row-label">{svc}</span>
              <span className="closing-row-count">{count} txn{count > 1 ? 's' : ''}</span>
              <span className="closing-row-total" style={{ color: '#0d6e6a' }}>₹{total.toLocaleString('en-IN')}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="closing-grand-total">
        <span>OP / Counter Collected Today</span>
        <span style={{ fontFamily: 'Outfit, sans-serif', fontSize: 28, fontWeight: 900, color: '#10b981' }}>₹{grandTotal.toLocaleString('en-IN')}</span>
      </div>

      {ipClosingTotal > 0 && (
        <div className="closing-grand-total" style={{ marginTop: 12, borderColor: '#bfdbfe' }}>
          <span>IP Receipts Today (tracked separately — not in OP closing)</span>
          <span style={{ fontFamily: 'Outfit, sans-serif', fontSize: 22, fontWeight: 900, color: '#2563eb' }}>₹{ipClosingTotal.toLocaleString('en-IN')}</span>
        </div>
      )}

      {todayExpenses.length > 0 && (
        <div className="closing-grand-total" style={{ marginTop: 12, borderColor: '#fecaca' }}>
          <span>Expenses Today ({todayExpenses.length})</span>
          <span style={{ fontFamily: 'Outfit, sans-serif', fontSize: 22, fontWeight: 900, color: '#ef4444' }}>₹{expenseTotal.toLocaleString('en-IN')}</span>
        </div>
      )}

      <div className="closing-grand-total" style={{ marginTop: 12 }}>
        <span>Net Cash Position (Collections − Expenses)</span>
        <span style={{ fontFamily: 'Outfit, sans-serif', fontSize: 22, fontWeight: 900, color: grandTotal - expenseTotal >= 0 ? '#0d6e6a' : '#ef4444' }}>
          ₹{(grandTotal - expenseTotal).toLocaleString('en-IN')}
        </span>
      </div>

      <div className="card mt">
        <h3 style={{ marginTop: 0 }}>Today's Transactions ({todayReceipts.length})</h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                {['#', 'Patient', 'Service', 'Mode', 'Amount', 'Time'].map((h) => (
                  <th key={h} style={{ padding: '8px 10px', textAlign: 'left', border: '1px solid #e2e8f0', fontWeight: 700, color: '#475569' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {todayReceipts.map((r, i) => (
                <tr key={r.receiptId || i}>
                  <td style={{ padding: '7px 10px', border: '1px solid #f1f5f9', color: '#94a3b8', fontSize: 11 }}>{i + 1}</td>
                  <td style={{ padding: '7px 10px', border: '1px solid #f1f5f9', fontWeight: 600 }}>{r.patientName || r.patient}</td>
                  <td style={{ padding: '7px 10px', border: '1px solid #f1f5f9' }}>{r.service || 'Consultation'}</td>
                  <td style={{ padding: '7px 10px', border: '1px solid #f1f5f9' }}>{r.paymentMode || r.mode || 'Cash'}</td>
                  <td style={{ padding: '7px 10px', border: '1px solid #f1f5f9', fontWeight: 700, color: '#10b981' }}>₹{Number(r.amount || r.total || 0).toLocaleString('en-IN')}</td>
                  <td style={{ padding: '7px 10px', border: '1px solid #f1f5f9', color: '#64748b' }}>{(r.createdAt || '').slice(11, 16)}</td>
                </tr>
              ))}
              {todayReceipts.length === 0 && <tr><td colSpan={6} style={{ padding: 20, textAlign: 'center', color: '#94a3b8' }}>No receipts today</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* Expense Tracker Tab */
const EXPENSE_CATEGORIES = ['Salary', 'Rent', 'Electricity', 'Medical Supplies', 'Medicines', 'Equipment', 'Housekeeping', 'Food', 'Repairs', 'Marketing', 'Other'];

function ExpenseTab() {
  const { data, loading, error, reload } = useLiveData(() => ipApi.getExpenses(), []);
  const expenses = (data?.expenses || []).map((e) => ({ ...e, id: e.expenseId || e.id }));
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ description: '', category: 'Other', amount: '', date: new Date().toISOString().slice(0,10), paidBy: 'Cash', vendor: '' });

  const addExpense = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await ipApi.createExpense({ ...form, amount: Number(form.amount) });
      await reload();
      setShowForm(false);
      setForm({ description: '', category: 'Other', amount: '', date: new Date().toISOString().slice(0,10), paidBy: 'Cash', vendor: '' });
    } catch (err) {
      alert(err.message || 'Failed to save expense');
    } finally {
      setSaving(false);
    }
  };

  const totalExpenses = expenses.reduce((s, e) => s + Number(e.amount || 0), 0);

  const byCat = EXPENSE_CATEGORIES.map((cat) => {
    const catExp = expenses.filter((e) => e.category === cat);
    return { cat, total: catExp.reduce((s, e) => s + Number(e.amount || 0), 0), count: catExp.length };
  }).filter((c) => c.count > 0).sort((a, b) => b.total - a.total);

  return (
    <div>
      {error && <p className="hint" style={{ color: '#dc2626', marginBottom: 10 }}>{error} <button type="button" className="btn btn-sm btn-secondary" onClick={reload}>Retry</button></p>}
      {loading && expenses.length === 0 && <p className="hint">Loading expenses…</p>}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div className="closing-grand-total" style={{ flex: 1, margin: 0 }}>
          <span>Total Expenses</span>
          <span style={{ fontFamily: 'Outfit, sans-serif', fontSize: 24, fontWeight: 900, color: '#ef4444' }}>₹{totalExpenses.toLocaleString('en-IN')}</span>
        </div>
        <button type="button" className="btn btn-primary" onClick={() => setShowForm((s) => !s)} style={{ marginLeft: 12 }}><MdAdd /> Add Expense</button>
      </div>

      {showForm && (
        <form className="card mb form-grid" onSubmit={addExpense}>
          <h3 style={{ gridColumn: '1/-1', marginTop: 0 }}>Add Expense</h3>
          <div className="field full"><label>Description *</label><input className="input" required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="e.g. Electricity Bill, Staff Salary" /></div>
          <div className="field"><label>Category</label>
            <select className="input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
              {EXPENSE_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div className="field"><label>Amount ₹ *</label><input type="number" min={1} className="input" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></div>
          <div className="field"><label>Date</label><input type="date" className="input" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
          <div className="field"><label>Paid by</label>
            <select className="input" value={form.paidBy} onChange={(e) => setForm({ ...form, paidBy: e.target.value })}>
              {['Cash', 'Bank Transfer', 'UPI', 'Cheque', 'Card'].map((m) => <option key={m}>{m}</option>)}
            </select>
          </div>
          <div className="field"><label>Vendor / Payee</label><input className="input" value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} /></div>
          <div className="form-actions full">
            <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save Expense'}</button>
          </div>
        </form>
      )}

      <div className="closing-report-grid">
        <div className="closing-section">
          <div className="closing-section-title">Category Breakdown</div>
          {byCat.map(({ cat, total, count }) => (
            <div key={cat} className="closing-row">
              <span className="closing-row-label">{cat}</span>
              <span className="closing-row-count">{count} item{count > 1 ? 's' : ''}</span>
              <span className="closing-row-total" style={{ color: '#ef4444' }}>₹{total.toLocaleString('en-IN')}</span>
            </div>
          ))}
          {byCat.length === 0 && <p className="hint">No expenses recorded.</p>}
        </div>
        <div className="closing-section">
          <div className="closing-section-title">All Expenses</div>
          {expenses.map((exp) => (
            <div key={exp.id} className="closing-row" style={{ flexWrap: 'wrap', gap: 4 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{exp.description}</div>
                <div style={{ fontSize: 11, color: '#94a3b8' }}>{exp.category} · {exp.date} · {exp.paidBy}{exp.vendor ? ` · ${exp.vendor}` : ''}</div>
              </div>
              <span className="closing-row-total" style={{ color: '#ef4444', fontSize: 14 }}>₹{Number(exp.amount).toLocaleString('en-IN')}</span>
            </div>
          ))}
          {expenses.length === 0 && <p className="hint">No expenses yet.</p>}
        </div>
      </div>
    </div>
  );
}

/* Doctor Revenue Split Tab */
function DoctorRevenueTab({ receipts }) {
  const doctorRevenue = useMemo(() => {
    const byDoctor = {};
    (receipts || []).forEach((r) => {
      const name = String(r.doctor || 'Unassigned').trim() || 'Unassigned';
      if (!byDoctor[name]) byDoctor[name] = { name, dept: '—', patientCount: 0, revenue: 0 };
      byDoctor[name].patientCount += 1;
      byDoctor[name].revenue += Number(r.amount || 0);
    });
    return Object.values(byDoctor)
      .map((d) => {
        const hospitalShare = Math.round(d.revenue * 0.6);
        const doctorShare = d.revenue - hospitalShare;
        const consultFee = d.patientCount ? Math.round(d.revenue / d.patientCount) : 0;
        return { ...d, consultFee, hospitalShare, doctorShare };
      })
      .sort((a, b) => b.revenue - a.revenue);
  }, [receipts]);

  const totalRevenue = doctorRevenue.reduce((s, d) => s + d.revenue, 0);
  const totalDoctorPay = doctorRevenue.reduce((s, d) => s + d.doctorShare, 0);

  return (
    <div>
      <div className="closing-report-grid" style={{ marginBottom: 14 }}>
        <div className="closing-grand-total" style={{ margin: 0 }}>
          <span>Total Revenue</span>
          <span style={{ fontFamily: 'Outfit, sans-serif', fontSize: 22, fontWeight: 900, color: '#0d6e6a' }}>₹{totalRevenue.toLocaleString('en-IN')}</span>
        </div>
        <div className="closing-grand-total" style={{ margin: 0 }}>
          <span>Doctor Payouts</span>
          <span style={{ fontFamily: 'Outfit, sans-serif', fontSize: 22, fontWeight: 900, color: '#8b5cf6' }}>₹{totalDoctorPay.toLocaleString('en-IN')}</span>
        </div>
      </div>
      <div className="card">
        <h3 style={{ marginTop: 0 }}>Doctor-wise Revenue Split</h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                {['Doctor', 'Department', 'Patients', 'Gross Revenue', 'Hospital (60%)', 'Doctor (40%)'].map((h) => (
                  <th key={h} style={{ padding: '9px 12px', textAlign: 'left', border: '1px solid #e2e8f0', fontWeight: 700, color: '#475569', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {doctorRevenue.length === 0 && (
                <tr><td colSpan={6} style={{ padding: 16, textAlign: 'center', color: '#64748b' }}>No receipt data with doctor names yet. Add doctor on OPD receipts to see revenue split.</td></tr>
              )}
              {doctorRevenue.map((doc) => (
                <tr key={doc.name}>
                  <td style={{ padding: '9px 12px', border: '1px solid #f1f5f9', fontWeight: 700 }}>{doc.name}</td>
                  <td style={{ padding: '9px 12px', border: '1px solid #f1f5f9', color: '#64748b' }}>{doc.dept}</td>
                  <td style={{ padding: '9px 12px', border: '1px solid #f1f5f9', textAlign: 'center', fontWeight: 700 }}>{doc.patientCount}</td>
                  <td style={{ padding: '9px 12px', border: '1px solid #f1f5f9', fontWeight: 700 }}>₹{doc.revenue.toLocaleString('en-IN')}</td>
                  <td style={{ padding: '9px 12px', border: '1px solid #f1f5f9', color: '#10b981', fontWeight: 700 }}>₹{doc.hospitalShare.toLocaleString('en-IN')}</td>
                  <td style={{ padding: '9px 12px', border: '1px solid #f1f5f9', color: '#8b5cf6', fontWeight: 700 }}>₹{doc.doctorShare.toLocaleString('en-IN')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="hint mt" style={{ fontSize: 11 }}>* Revenue split ratio is indicative (60:40). Configure actual ratios in Settings.</p>
      </div>
    </div>
  );
}

/* Insurance / TPA Panel */
const CLAIM_STATUSES = ['Pending', 'Submitted', 'Under Review', 'Approved', 'Partially Approved', 'Rejected'];
const INSURERS = [
  'Star Health', 'HDFC ERGO', 'ICICI Lombard', 'New India Assurance',
  'United India', 'National Insurance', 'Oriental Insurance',
  'Bajaj Allianz', 'Reliance General', 'Care Health', 'Niva Bupa', 'Other',
];
const TPAS = [
  'Medi Assist', 'Paramount Health Services', 'Vipul MedCorp', 'E-Meditek',
  'Heritage Health TPA', 'Good Health', 'MD India', 'Raksha TPA', 'Other',
];

function InsuranceTab() {
  const { data, loading, error, reload } = useLiveData(() => ipApi.getTpaClaims(), []);
  const claims = (data?.claims || []).map((c) => ({
    ...c,
    id: c.claimId || c.id,
    patient: c.patientName || c.patient,
    claimAmt: c.claimedAmt ?? c.claimAmt,
    date: c.submittedAt || c.date,
  }));
  const [showAdd, setShowAdd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    patient: '', admissionId: '', insurer: 'Star Health', tpa: 'Medi Assist',
    policyNo: '', claimAmt: '', status: 'Pending', notes: '',
  });

  const add = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await ipApi.createTpaClaim({
        patient: form.patient,
        admissionId: form.admissionId,
        insurer: form.insurer,
        tpa: form.tpa,
        policyNo: form.policyNo,
        claimedAmt: Number(form.claimAmt),
        status: form.status,
        notes: form.notes,
      });
      await reload();
      setForm({ patient: '', admissionId: '', insurer: 'Star Health', tpa: 'Medi Assist', policyNo: '', claimAmt: '', status: 'Pending', notes: '' });
      setShowAdd(false);
    } catch (err) {
      alert(err.message || 'Failed to save claim');
    } finally {
      setSaving(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await ipApi.patchTpaClaim(id, { status });
      await reload();
    } catch (err) {
      alert(err.message || 'Failed to update status');
    }
  };

  const statusColor = (s) => {
    if (s === 'Approved') return { bg: '#f0fdf4', color: '#16a34a', border: '#86efac' };
    if (s === 'Rejected') return { bg: '#fef2f2', color: '#dc2626', border: '#fca5a5' };
    if (s === 'Submitted' || s === 'Under Review') return { bg: '#eff6ff', color: '#2563eb', border: '#93c5fd' };
    if (s === 'Partially Approved') return { bg: '#fff7ed', color: '#ea580c', border: '#fed7aa' };
    return { bg: '#f8fafc', color: '#475569', border: '#e2e8f0' };
  };

  const totalClaimed = claims.reduce((s, c) => s + Number(c.claimAmt || 0), 0);
  const totalApproved = claims.filter((c) => c.status === 'Approved').reduce((s, c) => s + Number(c.claimAmt || 0), 0);
  const pendingCount = claims.filter((c) => ['Pending', 'Submitted', 'Under Review'].includes(c.status)).length;

  return (
    <div>
      {error && <p className="hint" style={{ color: '#dc2626', marginBottom: 10 }}>{error} <button type="button" className="btn btn-sm btn-secondary" onClick={reload}>Retry</button></p>}
      {loading && claims.length === 0 && <p className="hint">Loading claims…</p>}
      <div className="ins-stats">
        <div className="ins-stat">
          <MdShield style={{ color: '#2563eb', fontSize: 20 }} />
          <div className="ins-stat-val">{claims.length}</div>
          <div className="ins-stat-label">Total Claims</div>
        </div>
        <div className="ins-stat">
          <MdAccountBalanceWallet style={{ color: '#10b981', fontSize: 20 }} />
          <div className="ins-stat-val">₹{totalClaimed.toLocaleString('en-IN')}</div>
          <div className="ins-stat-label">Total Claimed</div>
        </div>
        <div className="ins-stat">
          <MdBusiness style={{ color: '#059669', fontSize: 20 }} />
          <div className="ins-stat-val">₹{totalApproved.toLocaleString('en-IN')}</div>
          <div className="ins-stat-label">Approved</div>
        </div>
        <div className="ins-stat">
          <MdReceiptLong style={{ color: '#f59e0b', fontSize: 20 }} />
          <div className="ins-stat-val">{pendingCount}</div>
          <div className="ins-stat-label">Pending</div>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <button type="button" className="btn btn-primary" onClick={() => setShowAdd((s) => !s)}>
          <MdAdd /> {showAdd ? 'Cancel' : 'Add Claim'}
        </button>
      </div>

      {showAdd && (
        <form className="form-grid card mb" onSubmit={add}>
          <h3 style={{ gridColumn: '1/-1', marginTop: 0 }}>New Insurance / TPA Claim</h3>
          <div className="field"><label>Patient name *</label><input className="input" required value={form.patient} onChange={(e) => setForm({ ...form, patient: e.target.value })} /></div>
          <div className="field"><label>Admission ID</label><input className="input" value={form.admissionId} onChange={(e) => setForm({ ...form, admissionId: e.target.value })} /></div>
          <div className="field">
            <label>Insurance company</label>
            <select className="input" value={form.insurer} onChange={(e) => setForm({ ...form, insurer: e.target.value })}>
              {INSURERS.map((i) => <option key={i}>{i}</option>)}
            </select>
          </div>
          <div className="field">
            <label>TPA</label>
            <select className="input" value={form.tpa} onChange={(e) => setForm({ ...form, tpa: e.target.value })}>
              {TPAS.map((t) => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div className="field"><label>Policy / Member ID</label><input className="input" value={form.policyNo} onChange={(e) => setForm({ ...form, policyNo: e.target.value })} /></div>
          <div className="field"><label>Claim amount ₹</label><input className="input" type="number" value={form.claimAmt} onChange={(e) => setForm({ ...form, claimAmt: e.target.value })} /></div>
          <div className="field full"><label>Notes (preauth #, remarks)</label><input className="input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="e.g. PA-12345, diagnosis code..." /></div>
          <div className="form-actions full"><button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Add Claim'}</button></div>
        </form>
      )}

      <div className="ins-claims-list">
        {claims.map((cl) => {
          const sc = statusColor(cl.status);
          return (
            <div key={cl.id} className="ins-claim-card">
              <div className="ins-claim-head">
                <div>
                  <div className="ins-claim-patient">{cl.patient}</div>
                  <div className="ins-claim-meta">
                    <MdShield style={{ fontSize: 12, verticalAlign: '-1px', marginRight: 3, color: '#2563eb' }} />
                    {cl.insurer} · TPA: {cl.tpa}
                  </div>
                  <div className="ins-claim-meta">Policy: {cl.policyNo || '—'} · {cl.date}</div>
                  {cl.admissionId && <div className="ins-claim-meta">Admission: {cl.admissionId}</div>}
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div className="ins-claim-amount">₹{Number(cl.claimAmt || 0).toLocaleString('en-IN')}</div>
                  <span className="ins-status-chip" style={{ background: sc.bg, color: sc.color, border: `1px solid ${sc.border}` }}>
                    {cl.status}
                  </span>
                </div>
              </div>
              {cl.notes && <div className="ins-claim-notes">{cl.notes}</div>}
              <div className="ins-claim-actions">
                <span style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>Update status:</span>
                {CLAIM_STATUSES.map((s) => (
                  <button
                    key={s}
                    type="button"
                    className={`ins-status-btn ${cl.status === s ? 'active' : ''}`}
                    onClick={() => updateStatus(cl.id, s)}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
        {claims.length === 0 && (
          <p style={{ color: '#94a3b8', textAlign: 'center', padding: 24 }}>
            No claims yet. Click "Add Claim" to start tracking.
          </p>
        )}
      </div>
    </div>
  );
}

/* Package Billing Tab */
function PackageBillingTab() {
  const { user } = useAuth();
  const { settings: emrSettings } = useEmrSettings();
  const PACKAGES = useMemo(() => packagesFromSettings(emrSettings), [emrSettings]);
  const inrFmt = (n) => '\u20b9' + Number(n || 0).toLocaleString('en-IN');
  const { records: bookingRecords, create, loading: bookingsLoading } = useHmsRecords('package-booking');
  const [search, setSearch] = React.useState('');
  const [deptFilter, setDeptFilter] = React.useState('All');
  const [showBook, setShowBook] = React.useState(false);
  const [selected, setSelected] = React.useState(null);
  const [bookForm, setBookForm] = React.useState({ patient: '', ipId: '', advance: '', discount: '' });
  const [busy, setBusy] = React.useState(false);
  const [msg, setMsg] = React.useState('');

  const bookings = bookingRecords.map((r) => ({
    id: r.recordId || r.id,
    package: r.packageName || r.package,
    patient: r.patient,
    ipId: r.ipId,
    amount: Number(r.amount || 0),
    discount: Number(r.discount || 0),
    finalAmt: Number(r.finalAmt || 0),
    advance: Number(r.advance || 0),
    balance: Number(r.balance || 0),
    status: r.status || 'Active',
  }));

  const depts = ['All', ...new Set(PACKAGES.map(p => p.dept))];
  const filtered = PACKAGES.filter(p =>
    (deptFilter === 'All' || p.dept === deptFilter) &&
    (search === '' || p.name.toLowerCase().includes(search.toLowerCase()))
  );

  async function bookPackage(e) {
    e.preventDefault();
    if (!selected) return;
    const disc = Number(bookForm.discount) || 0;
    const finalAmt = selected.amount - disc;
    const adv = Number(bookForm.advance) || 0;
    setBusy(true);
    setMsg('');
    try {
      const receiptAmt = adv > 0 ? adv : finalAmt;
      if (receiptAmt > 0) {
        await ipApi.createOpReceipt({
          patientName: bookForm.patient.trim(),
          patientId: bookForm.ipId || undefined,
          amount: receiptAmt,
          paymentMode: 'Cash',
          service: `Health Package: ${selected.name}`,
          desc: `Package booking — total ${inrFmt(finalAmt)}, advance ${inrFmt(adv)}, balance ${inrFmt(Math.max(0, finalAmt - adv))}`,
          notes: `Package ID: ${selected.id}`,
          hospitalId: user?.hospitalId,
        });
      }
      await create({
        packageName: selected.name,
        packageId: selected.id,
        patient: bookForm.patient,
        ipId: bookForm.ipId,
        amount: selected.amount,
        discount: disc,
        finalAmt,
        advance: adv,
        balance: Math.max(0, finalAmt - adv),
        status: 'Active',
        bookedAt: new Date().toISOString(),
      });
      setShowBook(false);
      setSelected(null);
      setBookForm({ patient: '', ipId: '', advance: '', discount: '' });
      setMsg(receiptAmt > 0 ? 'Package booked and receipt created.' : 'Package booked.');
    } catch (err) {
      setMsg(err.message || 'Could not book package');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      {msg && <div className={`mb ${/fail|error|could/i.test(msg) ? 'error' : 'info-banner'}`}>{msg}</div>}
      <div className="info-banner mb">
        Package catalog is hospital-configured. Bookings are saved to your HCEN hospital records.
      </div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <input className="input" style={{ flex: 1, minWidth: 200 }} placeholder="Search packages..." value={search} onChange={e => setSearch(e.target.value)} />
        <select className="input" style={{ width: 'auto' }} value={deptFilter} onChange={e => setDeptFilter(e.target.value)}>{depts.map(d => <option key={d}>{d}</option>)}</select>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(260px,1fr))', gap: 12, marginBottom: 20 }}>
        {filtered.length === 0 && (
          <p className="hint" style={{ gridColumn: '1/-1' }}>No packages configured. Manager can add them under Rx pad settings → Treatment packages.</p>
        )}
        {filtered.map(pkg => (
          <div key={pkg.id} className="card" style={{ cursor: 'pointer', border: selected && selected.id === pkg.id ? '2px solid #0d6e6a' : '1px solid #e2e8f0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <div style={{ fontWeight: 800, fontSize: 13 }}>{pkg.name}</div>
              <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 999, background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe' }}>{pkg.dept}</span>
            </div>
            <div style={{ fontFamily: 'Outfit,sans-serif', fontSize: 22, fontWeight: 900, color: '#0d6e6a', marginBottom: 4 }}>{inrFmt(pkg.amount)}</div>
            <div style={{ fontSize: 11.5, color: '#64748b', marginBottom: 4 }}>{pkg.includes}</div>
            <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 8 }}>Duration: {pkg.duration}</div>
            <button type="button" className="btn btn-primary btn-sm" style={{ width: '100%' }} onClick={() => { setSelected(pkg); setShowBook(true); }}>Book Package</button>
          </div>
        ))}
      </div>
      {showBook && selected && (
        <form className="card mb" style={{ borderLeft: '4px solid #0d6e6a' }} onSubmit={bookPackage}>
          <h3 style={{ marginTop: 0, color: '#0d6e6a' }}>Book: {selected.name} - {inrFmt(selected.amount)}</h3>
          <div className="form-grid mb">
            <div className="field"><label>Patient Name *</label><input className="input" required value={bookForm.patient} onChange={e => setBookForm(f => ({ ...f, patient: e.target.value }))} /></div>
            <div className="field"><label>IP/OP ID</label><input className="input" value={bookForm.ipId} onChange={e => setBookForm(f => ({ ...f, ipId: e.target.value }))} /></div>
            <div className="field"><label>Discount (\u20b9)</label><input type="number" className="input" value={bookForm.discount} onChange={e => setBookForm(f => ({ ...f, discount: e.target.value }))} placeholder="0" /></div>
            <div className="field"><label>Advance Paid (\u20b9)</label><input type="number" className="input" value={bookForm.advance} onChange={e => setBookForm(f => ({ ...f, advance: e.target.value }))} /></div>
          </div>
          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => { setShowBook(false); setSelected(null); }}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? 'Saving…' : 'Confirm Booking'}</button>
          </div>
        </form>
      )}
      {bookingsLoading && <p className="hint mb">Loading package bookings…</p>}
      {bookings.length > 0 && (
        <div className="card">
          <h3 style={{ marginTop: 0 }}>Active Package Bookings</h3>
          <div className="table-wrap">
            <table className="data-table">
              <thead><tr><th>ID</th><th>Package</th><th>Patient</th><th>Amount</th><th>Discount</th><th>Final</th><th>Balance</th><th>Status</th></tr></thead>
              <tbody>{bookings.map(b => (
                <tr key={b.id}><td style={{ fontWeight:600, color:'#6366f1' }}>{b.id}</td><td>{b.package}</td><td>{b.patient}</td><td>{inrFmt(b.amount)}</td><td style={{ color:'#10b981' }}>-{inrFmt(b.discount)}</td><td style={{ fontWeight:700 }}>{inrFmt(b.finalAmt)}</td><td style={{ fontWeight:700, color: b.balance > 0 ? '#ef4444' : '#10b981' }}>{inrFmt(b.balance)}</td><td><span style={{ fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:999, background:'#f0fdf4', color:'#16a34a', border:'1px solid #86efac' }}>{b.status}</span></td></tr>
              ))}</tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

/* Day Sheet Tab — live receipts */
function DaySheetTab() {
  const inrFmt = (n) => '\u20b9' + Number(n || 0).toLocaleString('en-IN');
  const [date, setDate] = React.useState(new Date().toISOString().slice(0, 10));
  const { data, loading } = useLiveData(() => ipApi.getReceipts(300), []);
  const receipts = React.useMemo(() => {
    const all = data?.receipts || [];
    return all.filter((r) => (r.createdAt || '').slice(0, 10) === date);
  }, [data, date]);

  const dayData = React.useMemo(() => {
    const agg = { opdBilling: 0, ipdBilling: 0, pharmacy: 0, lab: 0, radiology: 0, cash: 0, upi: 0, card: 0, insurance: 0, refunds: 0 };
    receipts.forEach((r) => {
      const amt = Number(r.amount || 0);
      const mode = (r.paymentMode || 'Cash').toLowerCase();
      const svc = (r.service || r.type || '').toLowerCase();
      if (mode.includes('upi') || mode.includes('online')) agg.upi += amt;
      else if (mode.includes('card')) agg.card += amt;
      else if (mode.includes('insur') || mode.includes('tpa')) agg.insurance += amt;
      else agg.cash += amt;
      if (r.type === 'IP' || svc.includes('ipd') || svc.includes('admission')) agg.ipdBilling += amt;
      else if (svc.includes('pharm')) agg.pharmacy += amt;
      else if (svc.includes('lab')) agg.lab += amt;
      else if (svc.includes('radio') || svc.includes('x-ray') || svc.includes('ct') || svc.includes('mri')) agg.radiology += amt;
      else agg.opdBilling += amt;
      if (amt < 0) agg.refunds += Math.abs(amt);
    });
    return agg;
  }, [receipts]);

  const totalCollection = dayData.cash + dayData.upi + dayData.card + dayData.insurance;
  const transactions = receipts.slice(0, 50).map((r) => ({
    id: r.receiptId,
    time: r.createdAt ? new Date(r.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '—',
    patient: r.patientName,
    type: r.service || r.type || 'Receipt',
    mode: r.paymentMode || 'Cash',
    amount: Number(r.amount || 0),
  }));
  return (
    <div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center' }}>
        <label style={{ fontWeight: 700 }}>Date:</label>
        <input type="date" className="input" style={{ width: 'auto' }} value={date} onChange={e => setDate(e.target.value)} />
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => window.print()}>Print Day Sheet</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 10, marginBottom: 16 }}>
        {[{ label: 'Total Collection', val: inrFmt(totalCollection), color: '#10b981' }, { label: 'OPD Billing', val: inrFmt(dayData.opdBilling), color: '#6366f1' }, { label: 'IPD Billing', val: inrFmt(dayData.ipdBilling), color: '#3b82f6' }, { label: 'Pharmacy', val: inrFmt(dayData.pharmacy), color: '#f59e0b' }].map(s => (
          <div key={s.label} className="card" style={{ textAlign: 'center', padding: 14, borderTop: '3px solid ' + s.color }}>
            <div style={{ fontFamily: 'Outfit,sans-serif', fontSize: 18, fontWeight: 900, color: s.color }}>{s.val}</div>
            <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', marginTop: 3 }}>{s.label}</div>
          </div>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
        <div className="card">
          <h4 style={{ marginTop: 0 }}>Collection by Mode</h4>
          {[['Cash', dayData.cash, '#10b981'], ['UPI / Online', dayData.upi, '#6366f1'], ['Card', dayData.card, '#3b82f6'], ['Insurance / TPA', dayData.insurance, '#f59e0b']].map(function(item) {
            var label = item[0]; var val = item[1]; var color = item[2];
            return (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid #f1f5f9' }}>
                <span style={{ fontSize: 13 }}>{label}</span>
                <span style={{ fontWeight: 700, color: color }}>{inrFmt(val)}</span>
              </div>
            );
          })}
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderTop: '2px solid #0d6e6a', marginTop: 4 }}>
            <span style={{ fontWeight: 800 }}>Total</span>
            <span style={{ fontWeight: 900, color: '#0d6e6a', fontFamily: 'Outfit,sans-serif', fontSize: 16 }}>{inrFmt(totalCollection)}</span>
          </div>
        </div>
        <div className="card">
          <h4 style={{ marginTop: 0 }}>Department Revenue</h4>
          {[['OPD', dayData.opdBilling, '#6366f1'], ['IPD', dayData.ipdBilling, '#3b82f6'], ['Pharmacy', dayData.pharmacy, '#f59e0b'], ['Lab', dayData.lab, '#10b981'], ['Radiology', dayData.radiology, '#f97316']].map(function(item) {
            var dept = item[0]; var val = item[1]; var color = item[2];
            return (
              <div key={dept} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid #f1f5f9' }}>
                <span style={{ fontSize: 13 }}>{dept}</span>
                <span style={{ fontWeight: 700, color: color }}>{inrFmt(val)}</span>
              </div>
            );
          })}
        </div>
      </div>
      <div className="card">
        <h3 style={{ marginTop: 0 }}>Today's Transactions</h3>
        <div className="table-wrap">
          <table className="data-table">
            <thead><tr><th>Time</th><th>Txn ID</th><th>Patient</th><th>Type</th><th>Mode</th><th>Amount</th></tr></thead>
            <tbody>{transactions.map(t => (
              <tr key={t.id}><td style={{ fontWeight:600 }}>{t.time}</td><td style={{ color:'#6366f1', fontWeight:600 }}>{t.id}</td><td>{t.patient}</td><td>{t.type}</td><td><span style={{ fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:999, background:'#f8fafc', color:'#475569', border:'1px solid #e2e8f0' }}>{t.mode}</span></td><td style={{ fontWeight:700, color:'#10b981' }}>{inrFmt(t.amount)}</td></tr>
            ))}</tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
